import React from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  InputAdornment,
  Popover,
  TextField,
  Typography,
} from '@mui/material';
import { useFormikContext } from 'formik';
import { toast } from 'react-toastify';
import { important } from '../../../utils/global/helper';
import { VocabularyAttribute } from '../../../types/catalog';
import {
  MAX_PRODUCT_VARIANTS,
  ProductFormValues,
  combinationCount,
  generateVariantRows,
  setPriceForValue,
  valueLabelsFor,
} from '../../../utils/catalog/productForm';

interface Props {
  attribute: VocabularyAttribute;
}

/** The values a varying attribute comes in; a chip click prices every row with that value. */
export default function AxisChips({ attribute }: Props) {
  const { values, setFieldValue } = useFormikContext<ProductFormValues>();
  const [anchor, setAnchor] = React.useState<HTMLElement | null>(null);
  const [target, setTarget] = React.useState<string | null>(null);
  const [priceInput, setPriceInput] = React.useState('');

  const setAxis = (selected: string[]) => {
    const cleaned = [...new Set(selected.map(v => v.trim()).filter(Boolean))];
    const axisValues = { ...values.axisValues, [attribute.key]: cleaned };
    const count = combinationCount(values.varyingKeys, axisValues);
    if (count > MAX_PRODUCT_VARIANTS) {
      toast.error(
        `That makes ${count.toLocaleString()} variants. A product can have up to ${MAX_PRODUCT_VARIANTS.toLocaleString()}.`,
      );
      return;
    }
    setFieldValue('axisValues', axisValues);
    setFieldValue(
      'variants',
      generateVariantRows(values.variants, values.varyingKeys, axisValues),
    );
  };

  const placeholder = `Add the ${attribute.label.toLowerCase()} options you carry`;

  const rowCount = target
    ? values.variants.filter(r => r.values[attribute.key] === target).length
    : 0;

  const applyPrice = () => {
    if (!target) return;
    const price =
      priceInput.trim() === '' ? '' : Math.max(0, Number(priceInput));
    setFieldValue(
      'variants',
      setPriceForValue(values.variants, attribute.key, target, price),
    );
    setTarget(null);
  };

  return (
    <>
      <Autocomplete
        multiple
        fullWidth
        freeSolo
        sx={{
          // form.css pins inputs inside .input rows to 50px, which clips wrapped chips
          '& .MuiOutlinedInput-root': {
            height: important('auto'),
            minHeight: 50,
            padding: important('6px 40px 6px 8px'),
            flexWrap: 'wrap',
            alignItems: important('center'),
            alignContent: 'center',
            justifyContent: 'flex-start',
            rowGap: 0.5,
          },
          // the placeholder carries the instructions, so it wraps to its own line rather than clip
          '& .MuiInputBase-input': {
            height: important('auto'),
            padding: important('6px 4px'),
            textAlign: important('left'),
            minWidth: important(`min(${placeholder.length + 2}ch, 100%)`),
          },
          '& fieldset': { border: important(0), outline: important('none') },
          '& .MuiAutocomplete-input': {
            outline: important('none'),
            boxShadow: important('none'),
          },
        }}
        options={valueLabelsFor(attribute, values.category)}
        value={values.axisValues[attribute.key] ?? []}
        onChange={(_, next) => setAxis(next)}
        renderTags={(selected, getTagProps) =>
          selected.map((value, index) => {
            const { key: tagKey, ...tagProps } = getTagProps({ index });
            return (
              <Chip
                key={tagKey}
                size="small"
                label={value}
                {...tagProps}
                onClick={e => {
                  setPriceInput('');
                  setTarget(value);
                  setAnchor(e.currentTarget);
                }}
              />
            );
          })
        }
        renderInput={params => (
          <TextField {...params} placeholder={placeholder} />
        )}
      />
      <Popover
        open={!!target}
        anchorEl={anchor}
        onClose={() => setTarget(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}>
        <Box
          p={1.5}
          display="flex"
          flexDirection="column"
          gap={1}
          minWidth={240}>
          <Typography variant="subtitle2">
            Price for every {target} variant
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {rowCount} row{rowCount === 1 ? '' : 's'}
          </Typography>
          <TextField
            autoFocus
            size="small"
            type="number"
            value={priceInput}
            onChange={e => setPriceInput(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                e.preventDefault();
                applyPrice();
              }
            }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">$</InputAdornment>
                ),
              },
            }}
          />
          <Button variant="contained" size="small" onClick={applyPrice}>
            Apply
          </Button>
        </Box>
      </Popover>
    </>
  );
}
